import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronLeft, Phone, AlertCircle,User } from 'lucide-react';
import apiService from '../services/apiService';

interface SchedulingDetails {
  date: string;
  time: string;
  quartier: string;
}

interface OrderDetails {
  carModel: string;
  serviceType: 'simple' | 'complete';
  oilBrand: string;
  pricing: PricingDetails | undefined;
  phone: string | null;
  client_name: string | null;
  schedulingDetails: SchedulingDetails;
  id_brand: number;
}

interface ReservationSummaryProps {
  serviceDetails: OrderDetails;
  onNextStep: (details: OrderDetails & { orderNumber?: string }) => void;
  onBackStep: () => void;
}

interface PricingDetails {
  oil_price: { [key: string]: number };
  oil_filter_price: number;
  oil_capacity: string,
  oil_grade: string,
  air_filter_price?: number;
  fuel_filter_price?: number;
  labor_price: number;
  travel_price: number;
}

export default function ReservationSummary({ serviceDetails, onNextStep, onBackStep }: ReservationSummaryProps) {
  const { t } = useTranslation();
  const [paymentMethod, setPaymentMethod] = useState<'online' | 'onsite'>('online');
  const [fserviceDetails, setfServiceDetails] = useState<OrderDetails>(serviceDetails);
  const [error, setError] = useState('');
  const [phoneError, setPhoneError] = useState('');
  const [nameError, setNameError] = useState('');
  const [pricingDetails, setPricingDetails] = useState<PricingDetails | null>(null);
  const [loading, setLoading] = useState(false);

  // Fetch pricing details when component mounts
  useEffect(() => {
    const fetchPricing = async () => {
      try {
        setLoading(true);
        const response = await apiService.getPricing({
          id_brand: serviceDetails.id_brand,
          service_type: serviceDetails.serviceType
        });

        if (response.success) {
          setPricingDetails(response.data.prices);
        } else {
          setError(t('summary.pricingError'));
        }
      } catch (err) {
        console.error('Error fetching pricing:', err);
        setError(t('summary.pricingError'));
      } finally {
        setLoading(false);
      }
    };

    fetchPricing();
  }, [serviceDetails, t]);

  const validatePhone = (phone: string) => {
    // Moroccan phone number validation (starts with 06 or 07 followed by 8 digits)
    const cleanedPhone = phone.replace(/[\s\-\.]/g, ''); // Remove spaces, dashes, dots

    const phoneRegex = /^(?:\+212|00212|212|0)?(6|7)\d{8}$/;
    return phoneRegex.test(cleanedPhone);
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const phoneValue = e.target.value;
    setfServiceDetails({...fserviceDetails, phone: phoneValue});
    
    if (phoneValue && !validatePhone(phoneValue)) {
      setPhoneError(t('common.invalidPhone'));
    } else {
      setPhoneError('');
    }
  };

  const handleUserName = (e: React.ChangeEvent<HTMLInputElement>) => {
    const nameValue = e.target.value;
    setfServiceDetails({...fserviceDetails, client_name: nameValue});
    
    if (!nameValue || nameValue.trim().length < 2) {
      setNameError(t('common.invalidClientName'));
    } else {
      setNameError('');
    }
  };
  const calculateTotal = () => {
    if (!pricingDetails) return 0;

    const prices = [
      pricingDetails.oil_price[serviceDetails.oilBrand],
      pricingDetails.oil_filter_price,
      pricingDetails.labor_price,
      pricingDetails.travel_price
    ];

    // Add conditional filters for complete service
    if (serviceDetails.serviceType === 'complete') {
      prices.push(pricingDetails.air_filter_price || 0);
      prices.push(pricingDetails.fuel_filter_price || 0);
    }

    return prices.reduce((sum, price) => parseFloat(sum.toString()) + parseFloat(price.toString()), 0).toFixed(2);
  };

  const handleFinish = async () => {
    if (!fserviceDetails.client_name) {
      setNameError(t('common.invalidClientName'));
      return;
    }

    if (!fserviceDetails.phone) {
      setPhoneError(t('common.invalidPhone'));
      return;
    }

    if (!validatePhone(fserviceDetails.phone)) {
      setPhoneError(t('common.invalidPhone'));
      return;
    }

    try {
      setLoading(true);
      setError('');
      
      const reservationData = {
        id_brand: fserviceDetails.id_brand,
        service_type: fserviceDetails.serviceType,
        oil_brand: fserviceDetails.oilBrand,
        oil_price: pricingDetails?.oil_price[fserviceDetails.oilBrand] || 0,
        scheduled_date: fserviceDetails.schedulingDetails.date,
        scheduled_time: fserviceDetails.schedulingDetails.time,
        location: fserviceDetails.schedulingDetails.quartier,
        phone: fserviceDetails.phone,
        client_name: fserviceDetails.client_name,
        payment_method: paymentMethod
      };

      const response = await apiService.createReservation(reservationData);
      console.log(response);
      if (response.success) {
       //DOING PAYEMENT STUFF HERE
       if(paymentMethod=='online'){
        if (response.data.redirect_url) {
          console.log(response.redirect_url);
          // Create form dynamically
          const form = document.createElement("form");
          form.method = "POST";
          form.action = response.data.redirect_url;
          form.style.display = "none";
  
          // Append hidden input fields
          Object.entries(response.data.params).forEach(([key, value]) => {
            const input = document.createElement("input");
            input.type = "hidden";
            input.name = key;
            //@ts-ignore
            input.value = value;
            form.appendChild(input);
          });
          console.log(form);
  
          document.body.appendChild(form);
          console.log('SUBMITTING');
          form.submit(); // Redirect to CMI
        }
       }else
        onNextStep({
          ...fserviceDetails,
          orderNumber: response.data.order_number
        });
      } else {
        setError(response.message || t('summary.createError'));
      }
    } catch (err) {
      console.error('Error creating reservation:', err);
      setError(t('summary.createError'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex flex-col min-h-screen bg-[#ededed]">
      <div className="flex flex-col items-center px-4 max-w-md mx-auto w-full">
        <button
          onClick={onBackStep}
          className="self-start relative top-12 mb-4 flex items-center text-blue-500"
          aria-label={t('common.backButton')}
          disabled={loading}
        >
          <ChevronLeft className="w-5 h-5 mr-1" />
          {t('common.back')}
        </button>
        
        <h1 className="text-[24px] font-medium mb-6 text-center">
          {t('summary.title')}
        </h1>

        <div className="w-full bg-white rounded-lg border border-gray-200 p-6 mb-6">
          <h2 className="text-[16px] mb-2">
            {serviceDetails.serviceType === 'simple' ? t('home.simpleOilChange') : t('home.completeOilChange')} {t('common.for')} {serviceDetails.carModel}
          </h2>

          <div className="text-[16px] mb-2 flex justify-between">
            <span>
              {serviceDetails.schedulingDetails.date} {serviceDetails.schedulingDetails.time}{' '}
              <span>{serviceDetails.schedulingDetails.quartier}</span>
            </span>
          </div>

          <div className="space-y-2">
            <h3 className="font-medium mb-2">{t('summary.elements')}</h3>
            
            {pricingDetails ? (
              <>
                <div className="flex justify-between">
                  <span>{t('success.oil')} ({serviceDetails.oilBrand}) <b>{pricingDetails.oil_grade} {pricingDetails.oil_capacity}L</b></span>
                  <span>{pricingDetails.oil_price[serviceDetails.oilBrand]}</span>
                </div>
                
                <div className="flex justify-between">
                  <span>{t('success.oilFilter')}</span>
                  <span>{pricingDetails.oil_filter_price}</span>
                </div>
                
                {serviceDetails.serviceType === 'complete' && (
                  <>
                    <div className="flex justify-between">
                      <span>{t('success.airFilter')}</span>
                      <span>{pricingDetails.air_filter_price || '-'}</span>
                    </div>
                    
                    <div className="flex justify-between">
                      <span>{t('success.fuelFilter')}</span>
                      <span>{pricingDetails.fuel_filter_price || '-'}</span>
                    </div>
                  </>
                )}
                
                <div className="flex justify-between">
                  <span>{t('success.labor')}</span>
                  <span>{pricingDetails.labor_price}</span>
                </div>
                
                <div className="flex justify-between">
                  <span>{t('success.travel')}</span>
                  <span>{pricingDetails.travel_price}</span>
                </div>

                <div className="flex justify-between pt-2 border-t border-gray-200 font-semibold">
                  <span>{t('success.total')}</span>
                  <span>{calculateTotal()}</span>
                </div>
              </>
            ) : (
              <div className="flex items-center justify-center">
                <svg className="animate-spin h-5 w-5 mr-2 text-blue-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                {t('common.loading')}
              </div>
            )}
          </div>
        </div>

        <div className="w-full mb-8">
          <div className="relative mb-2">
            <div className="absolute inset-y-0 left-4 flex items-center pointer-events-none">
              <Phone className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="tel"
              value={fserviceDetails.phone || ''}
              onChange={handlePhoneChange}
              placeholder={t('success.phoneNumber')}
              className={`w-full h-12 pl-12 pr-4 border rounded-lg ${
                phoneError ? 'border-red-500 bg-red-50' : 'border-gray-200'
              }`}
              disabled={loading}
            />
          </div>
          
          {phoneError && (
            <div className="flex items-start mb-2 text-red-600 text-sm">
              <AlertCircle className="h-4 w-4 mr-1 mt-0.5 flex-shrink-0" />
              <span>{phoneError}</span>
            </div>
          )}

          <div className="relative mb-2">
            <div className="absolute inset-y-0 left-4 flex items-center pointer-events-none">
              <User className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="text"
              value={fserviceDetails.client_name || ''}
              onChange={handleUserName}
              placeholder={t('success.fullName')}
              className={`w-full h-12 pl-12 pr-4 border rounded-lg ${
                phoneError ? 'border-red-500 bg-red-50' : 'border-gray-200'
              }`}
              disabled={loading}
            />
          </div>
          
          {nameError && (
            <div className="flex items-start mb-2 text-red-600 text-sm">
              <AlertCircle className="h-4 w-4 mr-1 mt-0.5 flex-shrink-0" />
              <span>{nameError}</span>
            </div>
          )}

          
          <div className="px-5 w-full font-semibold my-2 py-0">
            {t('common.payment')}
          </div>
          
          <div className="flex-row flex space-x-2">
            <button
              className={`h-12 flex flex-grow items-center justify-between px-4 border rounded-lg ${
                paymentMethod === 'online' 
                  ? 'bg-blue-50 border-blue-500' 
                  : 'bg-white border-gray-200'
              } ${loading ? 'opacity-50 cursor-not-allowed' : ''}`}
              onClick={() => !loading && setPaymentMethod('online')}
              disabled={loading}
            >
              <span className="me-2">{t('success.online')}</span>
              <div className="w-6 h-6 rounded-full border-2 flex items-center justify-center">
                {paymentMethod === 'online' && (
                  <div className="w-3 h-3 rounded-full bg-blue-500" />
                )}
              </div>
            </button>
            
            <button
              className={`h-12 flex flex-grow items-center justify-between px-4 border rounded-lg ${
                paymentMethod === 'onsite' 
                  ? 'bg-blue-50 border-blue-500' 
                  : 'bg-white border-gray-200'
              } ${loading ? 'opacity-50 cursor-not-allowed' : ''}`}
              onClick={() => !loading && setPaymentMethod('onsite')}
              disabled={loading}
            >
              <span className="me-2">{t('success.onsite')}</span>
              <div className="w-6 h-6 rounded-full border-2 flex items-center justify-center">
                {paymentMethod === 'onsite' && (
                  <div className="w-3 h-3 rounded-full bg-blue-500" />
                )}
              </div>
            </button>
          </div>

          {error && (
            <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start">
              <AlertCircle className="h-5 w-5 mr-2 text-red-600 flex-shrink-0 mt-0.5" />
              <span className="text-red-600 text-sm">{error}</span>
            </div>
          )}
        </div>

        <button 
          className={`w-full max-w-xs py-4 px-6 rounded-full bg-white text-[#2196F3] border-[#2196F3] border-2 font-bold shadow-lg text-lg transition-colors ${
            (!fserviceDetails.phone || loading || !pricingDetails) ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-50'
          }`}
          onClick={handleFinish}
          disabled={!fserviceDetails.phone || loading || !pricingDetails}
        >
          {loading ? (
            <div className="flex items-center justify-center">
              <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-[#2196F3]" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              {t('common.loading')}
            </div>
          ) : (
            t('summary.finishReservation')
          )}
        </button>
      </div>
    </div>
  );
}